import type { AnyPgColumn } from "drizzle-orm/pg-core";
import {
  boolean,
  index,
  integer,
  numeric,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";

export const productCategories = pgTable("product_categories", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => nanoid()),
  name: text("name").notNull(),
  description: text("description"),
  iconKey: text("icon_key"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", {
    withTimezone: true,
    mode: "date",
  }).notNull(),
});

export const products = pgTable(
  "products",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    name: text("name").notNull(),
    slug: text("slug").notNull(),
    description: text("description"),
    imageKey: text("image_key"),
    isActive: boolean("is_active").notNull().default(true),
    lowStockThreshold: integer("low_stock_threshold").notNull().default(3),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("products_by_active").on(t.isActive),
    uniqueIndex("products_slug_unique").on(t.slug),
  ],
);

/** many-to-many: product ↔ category */
export const productCategoryLinks = pgTable(
  "product_category_links",
  {
    productId: text("product_id")
      .notNull()
      .references(() => products.id, { onDelete: "cascade" }),
    categoryId: text("category_id")
      .notNull()
      .references(() => productCategories.id, { onDelete: "restrict" }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    uniqueIndex("product_category_links_unique").on(t.productId, t.categoryId),
    index("product_category_links_by_product").on(t.productId),
    index("product_category_links_by_category").on(t.categoryId),
  ],
);

export const productVariants = pgTable(
  "product_variants",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    productId: text("product_id")
      .notNull()
      .references(() => products.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    sku: text("sku"),
    price: numeric("price", { precision: 12, scale: 2 }).notNull(),
    stockQuantity: integer("stock_quantity").notNull().default(0),
    /** หน่วยขาย — สต็อก/ออเดอร์นับด้วยหน่วยนี้ */
    stockUnitLabel: text("stock_unit_label").notNull().default("ຊິ້ນ"),
    /** หน่วยรับเข้า (optional) — ถ้ามี ใบรับเข้ากรอกจำนวนด้วยหน่วยนี้ */
    receiveUnitLabel: text("receive_unit_label"),
    /** จำนวนหน่วยขายต่อ 1 หน่วยรับเข้า — เช่น 1 มัด = 100 ชิ้น → 100 */
    receiveConversionFactor: numeric("receive_conversion_factor", {
      precision: 12,
      scale: 4,
    })
      .notNull()
      .default("1"),
    imageKey: text("image_key"),
    sortOrder: integer("sort_order").notNull().default(0),
    isActive: boolean("is_active").notNull().default(true),
    /** ชี้ variant คลังรวม — sale variant ใช้สต็อกจาก pool นี้ */
    inventoryVariantId: text("inventory_variant_id").references(
      (): AnyPgColumn => productVariants.id,
      { onDelete: "restrict" },
    ),
    /** จำนวนหน่วยคลังต่อ 1 หน่วยขาย — เช่น มัด 10 = 10 เส้น */
    unitsPerSale: numeric("units_per_sale", { precision: 12, scale: 4 })
      .notNull()
      .default("1"),
    /** ซ่อนจาก order picker — ใช้เป็นคลังรวม/รับเข้าเท่านั้น */
    isInventoryOnly: boolean("is_inventory_only").notNull().default(false),
    /** จำนวนขั้นต่ำก่อนเตือนใกล้หมด — ต่อ variant (Phase 10 reorder planning) */
    lowStockThreshold: integer("low_stock_threshold").notNull().default(3),
    /** simple = สินค้าเดี่ยว, bundle = เช็ด/ชุดสำเร็จรูป (Phase 11) */
    variantType: text("variant_type").notNull().default("simple"),
  },
  (t) => [
    index("product_variants_by_product").on(t.productId),
    index("product_variants_by_inventory_pool").on(t.inventoryVariantId),
  ],
);

/** BOM — ส่วนประกอบต่อ 1 ชุด (Phase 11) */
export const productBundleLines = pgTable(
  "product_bundle_lines",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    bundleVariantId: text("bundle_variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "cascade" }),
    componentVariantId: text("component_variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    quantity: integer("quantity").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
  },
  (t) => [
    uniqueIndex("product_bundle_lines_unique_component").on(
      t.bundleVariantId,
      t.componentVariantId,
    ),
    index("product_bundle_lines_by_bundle").on(t.bundleVariantId),
  ],
);

/** audit การประกอบชุด (Phase 11) */
export const bundleAssemblies = pgTable(
  "bundle_assemblies",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    bundleVariantId: text("bundle_variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    quantity: integer("quantity").notNull(),
    note: text("note"),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [index("bundle_assemblies_by_variant").on(t.bundleVariantId)],
);

export const stockMovements = pgTable(
  "stock_movements",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    variantId: text("variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    /** variant ขาย (ถ้าหักผ่าน sale SKU ที่ใช้คลังรวม) */
    saleVariantId: text("sale_variant_id").references(
      (): AnyPgColumn => productVariants.id,
      { onDelete: "set null" },
    ),
    quantityDelta: integer("quantity_delta").notNull(),
    reason: text("reason").notNull(),
    referenceId: text("reference_id"),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("stock_movements_by_variant").on(t.variantId),
    index("stock_movements_by_reference").on(t.referenceId),
  ],
);

export type StockMovementReason =
  | "order_paid"
  | "order_cancelled"
  | "order_returned"
  | "manual_adjust"
  | "goods_receipt"
  | "goods_receipt_void"
  | "import_order_receive"
  | "import_order_receive_void"
  | "bundle_assembly"
  | "bundle_assembly_void"
  | "stock_transfer_out"
  | "stock_transfer_in"
  | "custom_job_started"
  | "event_issue"
  | "event_return";

export type VariantType = "simple" | "bundle";
